import type { Composer } from 'vue-i18n'
import type { OpportunitySummary } from '@/api/opportunities'
import type { OperationsMetricId } from '@/api/snapshot'
import type { ContinuityMetricId, MetricTarget, ObjectiveStatus } from '@/types/intelligence'
import { METRIC_KEYS } from './metric-format'
import { CONTINUITY_KEYS } from './continuity-format'
import { metricName } from './analyst'
import { concentrationText, weekFiguresText } from './opportunity-text'

type Translate = Composer['t']

/** What one of Elfie's bubbles says, and the question it asks: null opens the panel empty. */
export interface CompanionBubble {
  key: string
  text: string
  question: string | null
}

/** Few enough that a reader sees them all in about a minute. */
export const MAX_BUBBLES = 8

/** Elfie introducing itself: what it is and what it can do. Offered even before a workspace has figures of its own. */
export const INTRODUCTIONS = ['hello', 'abilities'] as const

const BY_PRIORITY: Record<OpportunitySummary['priority'], number> = { HIGH: 0, MEDIUM: 1, LOW: 2 }
// An objective falling behind is the one worth a look first.
const BY_STATUS: Partial<Record<ObjectiveStatus, number>> = { BEHIND: 0, AT_RISK: 1, ON_TRACK: 2 }

const isOperationsMetric = (metricId: string): metricId is OperationsMetricId => metricId in METRIC_KEYS
const mostPressing = <T extends { priority: OpportunitySummary['priority'] }>(items: T[]): T | undefined =>
  [...items].sort((a, b) => BY_PRIORITY[a.priority] - BY_PRIORITY[b.priority])[0]

/** The question Elfie is asked about one measure: an Operations metric's move, or a care journey share's (DECISIONS.md #39). */
const questionAbout = (t: Translate, metricId: string): string | null => {
  if (isOperationsMetric(metricId)) return t(`analyst.suggestions.metric.${METRIC_KEYS[metricId]}`)
  return CONTINUITY_KEYS[metricId as ContinuityMetricId] ? t('analyst.suggestions.careMeasure', { measure: metricName(metricId, t) }) : null
}

/**
 * What Elfie offers in the corner (Dao, 1 Oct 2026): first who it is and
 * what it can do (Dao, 5 Oct 2026), then the workspace's own figures, then
 * questions any workspace can ask. Every bubble's question is one the engine
 * answers from the data today (P4 v1.5 change 5), and every figure in a
 * bubble is one the page could show: the open waiting-time opportunity's
 * (P3), where its rise is concentrated, the open next-step opportunity's
 * (DECISIONS.md #45), and the workspace's objectives (P5). A bubble with
 * nothing behind it is not offered, and one about what Elfie can do claims
 * only what it does.
 */
export const companionBubbles = (
  { opportunities, objectives }: { opportunities: OpportunitySummary[]; objectives: MetricTarget[] },
  t: Translate,
  locale: string,
): CompanionBubble[] => {
  const bubbles: CompanionBubble[] = [
    { key: 'hello', text: t('analyst.companion.hello'), question: null },
    { key: 'abilities', text: t('analyst.companion.abilities'), question: null },
  ]

  const waiting = mostPressing(opportunities.flatMap((item) => (item.kind === 'WAITING_TIME' ? [item] : [])))
  if (waiting) {
    bubbles.push({
      key: `rose:${waiting.id}`,
      // Two whole sentences: the week's figures, as Elfie Pulse words them, then the offer.
      text: `${weekFiguresText(t, waiting, locale)} ${t('analyst.companion.why')}`,
      question: t('analyst.suggestions.overview.waitingTime'),
    })
    if (waiting.concentration) {
      // Two whole sentences: the opportunity's own, then the offer.
      const { axis } = waiting.concentration
      bubbles.push({
        key: `where:${waiting.id}`,
        text: `${concentrationText(t, waiting.concentration, locale)} ${t(`analyst.companion.byAxis.${axis}`)}`,
        question: t(`analyst.suggestions.byAxis.${axis}`),
      })
    }
  }

  const nextStep = mostPressing(opportunities.flatMap((item) => (item.kind === 'NEXT_STEP' ? [item] : [])))
  if (nextStep) {
    bubbles.push({
      key: `nextStep:${nextStep.id}`,
      text: `${weekFiguresText(t, nextStep, locale)} ${t('analyst.companion.nextStep')}`,
      question: t('analyst.suggestions.careMeasure', { measure: metricName(nextStep.metricId, t) }),
    })
  }

  const objective = objectives
    .filter((item) => item.status in BY_STATUS && questionAbout(t, item.metricId) !== null)
    .sort((a, b) => (BY_STATUS[a.status] ?? 0) - (BY_STATUS[b.status] ?? 0))[0]
  if (objective) {
    bubbles.push({
      key: `objective:${objective.id}`,
      text: t('analyst.companion.objective', { title: objective.title }),
      question: questionAbout(t, objective.metricId),
    })
  }

  bubbles.push(
    { key: 'checked', text: t('analyst.companion.checked'), question: null },
    { key: 'compare', text: t('analyst.companion.compare'), question: t('analyst.suggestions.overview.comparePrevious') },
    { key: 'chronicCare', text: t('analyst.companion.chronicCare'), question: t('analyst.suggestions.overview.chronicCare') },
    { key: 'patientsSeen', text: t('analyst.companion.patientsSeen'), question: t('analyst.suggestions.overview.patientsSeen') },
  )
  return bubbles.slice(0, MAX_BUBBLES)
}
